import React, { useEffect, useMemo, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { Switch } from 'components/forms';
import { GenericMessage } from 'components/feedback';
import { usePanel } from 'hooks/usePanel';
import { attributionsPlateformesAPI } from 'services/swagger-api';
import { GetBriquesCollaborateurPlateformeResponse } from 'api/attributions-plateformes';
import { InlineError, runOperation } from 'back-office/shared/components/rolesUi';

interface BriquesMembreFormProps {
  /** Absent = mode GOUVERNANCE : la plateforme active de l'appelant fait foi
   * côté serveur (garde de gouvernance + plafond délégable). */
  plateformeId?: string;
  /** Clé d'adressage : l'attribution cible le COMPTE du membre. */
  abpUserId: string;
  collaborateurName: string;
  onSuccess?: () => void;
  /** Vrai quand le formulaire est embarqué dans un panel à deux vues (liste des
   * membres d'un cabinet) : le succès rend la main au parent sans fermer le panel. */
  stayOpenOnSuccess?: boolean;
}

/**
 * Panel « droits du membre dans la plateforme » : une ligne par brique avec
 * son état SUR LE COMPTE (référence) et son état ICI (toggle). Tant que la
 * sélection reste identique au compte, le membre hérite de ses évolutions ;
 * dès qu'une brique diffère, un override est posé pour cette plateforme
 * (ensemble EXACT — il peut restreindre comme élargir). Gardes côté serveur.
 */
export const BriquesCollaborateurForm: React.FC<BriquesMembreFormProps> = ({
  plateformeId,
  abpUserId,
  collaborateurName,
  onSuccess,
  stayOpenOnSuccess = false
}) => {
  const { setPanelActions, closePanel } = usePanel();

  const [etat, setEtat] = useState<GetBriquesCollaborateurPlateformeResponse | null>(null);
  const [briques, setBriques] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (plateformeId
      ? attributionsPlateformesAPI.getBriquesCollaborateur({ plateformeId: plateformeId, abpUserId })
      : attributionsPlateformesAPI.getBriquesCollaborateurGouvernance({ abpUserId })
    )
      .then((res) => {
        if (cancelled) return;
        setEtat(res);
        // Toujours ∩ briques attribuables : en gouvernance le compte peut dépasser
        // le plafond — une valeur non affichée ne doit jamais partir au serveur.
        setBriques((res.briquesPlateforme ?? res.briquesCompte).filter((b) => res.briquesAutorisees.includes(b)));
      })
      .catch((err) => {
        console.error('Erreur de chargement des briques du membre:', err);
        if (!cancelled) setError('Impossible de charger les droits de ce collaborateur.');
      });
    return () => {
      cancelled = true;
    };
  }, [plateformeId, abpUserId]);

  const selectionCompte = useMemo(
    () => (etat ? etat.briquesCompte.filter((b) => etat.briquesAutorisees.includes(b)) : []),
    [etat]
  );

  /** Les briques dont l'état ICI diffère de l'état SUR LE COMPTE. */
  const briquesDifferentes = useMemo(() => {
    if (!etat) return [];
    return etat.briquesAutorisees.filter(
      (b) => briques.includes(b) !== etat.briquesCompte.includes(b)
    );
  }, [etat, briques]);

  useEffect(() => {
    const avaitOverride = etat?.briquesPlateforme != null;
    // Sélection identique aux briques du compte = héritage (comparaison sur le
    // compte COMPLET : s'il porte une brique hors périmètre affichable, la
    // sélection ne peut pas lui être égale et un override explicite est posé).
    const memesQueLeCompte =
      etat != null &&
      briques.length === etat.briquesCompte.length &&
      etat.briquesCompte.every((b) => briques.includes(b));

    setPanelActions({
      closeLabel: 'COMMON.CANCEL',
      submitLabel: 'COMMON.VALIDATE',
      submitDisabled: isSaving || !etat,
      onSubmit: async () => {
        if (!etat) return;
        setIsSaving(true);
        let err: string | null = null;
        if (!memesQueLeCompte) {
          err = await runOperation(() =>
            plateformeId
              ? attributionsPlateformesAPI.setBriquesCollaborateur({
                  plateformeId: plateformeId,
                  abpUserId,
                  setBriquesCollaborateurPlateformeCommand: { briques }
                })
              : attributionsPlateformesAPI.setBriquesCollaborateurGouvernance({
                  abpUserId,
                  setBriquesCollaborateurPlateformeCommand: { briques }
                })
          );
        } else if (avaitOverride) {
          err = await runOperation(() =>
            plateformeId
              ? attributionsPlateformesAPI.resetBriquesCollaborateur({
                  plateformeId: plateformeId,
                  abpUserId
                })
              : attributionsPlateformesAPI.resetBriquesCollaborateurGouvernance({ abpUserId })
          );
        }
        setIsSaving(false);
        setError(err);
        if (!err) {
          onSuccess?.();
          if (!stayOpenOnSuccess) closePanel();
        }
      }
    });
  }, [setPanelActions, closePanel, onSuccess, stayOpenOnSuccess, plateformeId, abpUserId, etat, briques, isSaving]);

  if (!etat) {
    return error ? <InlineError message={error} /> : <p className="text-sm text-gray-500">Chargement…</p>;
  }

  const details = etat.briquesAutoriseesDetails?.length
    ? etat.briquesAutoriseesDetails
    : etat.briquesAutorisees.map((nom) => ({ nom, description: undefined }));

  const basculer = (brique: string, active: boolean) =>
    setBriques((prev) => (active ? [...prev, brique] : prev.filter((b) => b !== brique)));

  return (
    <div className="space-y-5">
      {briquesDifferentes.length > 0 && (
        <GenericMessage
          type="warning"
          dismissible={false}
          message={`Droits propres à cette plateforme — ${briquesDifferentes.length} brique${briquesDifferentes.length > 1 ? 's diffèrent' : ' diffère'} du compte : ${briquesDifferentes.join(', ')}. Les évolutions du compte ne s'appliqueront plus ici.`}
          actionButton={{
            label: 'Aligner sur le compte',
            variant: 'secondary',
            onClick: () => setBriques(selectionCompte)
          }}
        />
      )}

      <div>
        <div className="mb-2 flex items-baseline gap-2">
          <h4 className="text-sm font-semibold text-gray-900">Briques métier</h4>
          <span className="text-xs tabular-nums text-gray-400">
            {briques.length} activée{briques.length > 1 ? 's' : ''} sur {details.length}
          </span>
        </div>

        <div className="mb-1.5 flex items-center gap-3 px-4 text-[11px] font-medium uppercase tracking-wide text-gray-400">
          <span className="flex-1">Brique</span>
          <span className="w-28 text-center">Sur le compte</span>
          <span className="w-24 text-right">Ici</span>
        </div>

        <div className="divide-y overflow-hidden rounded-xl border">
          {details.map(({ nom, description }) => {
            const surLeCompte = etat.briquesCompte.includes(nom);
            const ici = briques.includes(nom);
            const differe = surLeCompte !== ici;
            return (
              <div
                key={nom}
                className={`flex items-center gap-3 px-4 py-3 ${differe ? 'bg-amber-50/60' : 'bg-white'}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                    {nom}
                    {differe && (
                      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                        Spécifique
                      </span>
                    )}
                  </p>
                  {description && <p className="text-xs text-gray-500">{description}</p>}
                </div>
                <span className="w-28 text-center">
                  <span
                    className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      surLeCompte ? 'bg-slate-200 text-slate-700' : 'bg-gray-100 text-gray-400'
                    }`}
                  >
                    {surLeCompte ? 'Activée' : 'Désactivée'}
                  </span>
                </span>
                <span className="flex w-24 items-center justify-end gap-2">
                  {differe && (
                    <button
                      type="button"
                      title="Revenir à l'état du compte"
                      className="rounded p-1 text-gray-400 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      onClick={() => basculer(nom, surLeCompte)}
                    >
                      <RotateCcw className="h-4 w-4" />
                    </button>
                  )}
                  <Switch name={`ici-${nom}`} checked={ici} onChange={(checked) => basculer(nom, checked)} />
                </span>
              </div>
            );
          })}
        </div>

        {briques.length === 0 && (
          <p className="mt-2 text-xs text-amber-600">
            Aucune brique : ce membre n'aura AUCUN droit dans cette plateforme.
          </p>
        )}
      </div>

      <p className="text-xs text-gray-500">
        Tant que la sélection reste identique au compte, {collaborateurName} hérite automatiquement de
        toute évolution de ses rôles. Dès qu'une brique diffère, cette plateforme garde ses
        droits propres.
      </p>

      <InlineError message={error} />
    </div>
  );
};
